"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dispatch,
    SetStateAction,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { GetLomdaV3ResponseType } from "../../hooks/useGetLomdaV3";
import { Button } from "@/components/ui/button";
import {
    ChevronDown,
    FolderCheck,
    FolderEdit,
    FolderPlus,
    LoaderCircle,
    Trash,
} from "lucide-react";
import { useStorage } from "@/features/storage/useStorage";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { useQueryClient } from "@tanstack/react-query";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

export default function GeneralTab({
    lomda,
}: {
    lomda: GetLomdaV3ResponseType["data"];
}) {
    const [name, setName] = useState(lomda.name || "");
    const [folders, setFolders] = useState(lomda.folders);

    const [changes, setChanges] = useState(false);
    const [loading, setLoading] = useState(false);

    const queryClient = useQueryClient();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setLoading(true);

        const res = await client.api.storage["lomda-v3"][":lomdaId"].$patch({
            param: { lomdaId: lomda.id },
            json: {
                name,
                folderIds: folders.map((folder) => folder.id),
            },
        });

        if (!res.ok) {
            toast.error(t("toast.error.update-lomda"), {
                keyName: "toast.error.update-lomda",
            });
            setLoading(false);
            return;
        }

        toast.success(t("toast.success.changes-saved"), {
            keyName: "toast.success.changes-saved",
        });

        queryClient.invalidateQueries({ queryKey: ["get-storage-files"] });
        queryClient.invalidateQueries({ queryKey: ["get-storage-folders"] });
        queryClient.invalidateQueries({
            queryKey: ["get-lomda-v3", lomda.id],
        });
        setLoading(false);
        setChanges(false);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="mt-4 flex flex-col w-full gap-8"
        >
            <div className="flex flex-col md:flex-row gap-4">
                <div className="flex flex-col w-full md:w-1/2 gap-2">
                    <Label htmlFor="name">
                        {t(
                            "lomda-sheet.general-tab.lomda-name.label",
                            undefined,
                            undefined
                        )}
                    </Label>
                    <Input
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setChanges(true);
                        }}
                        className="mt-0.5"
                        id="name"
                        required
                    />
                </div>
                <div className="flex flex-col w-full md:w-1/2 gap-2">
                    <Label htmlFor="selectFolder">
                        {t("lomda-sheet.folders.label", undefined, undefined)}
                    </Label>
                    <div className="flex flex-row gap-2 items-center">
                        <FolderDropdown
                            folders={folders}
                            setFolders={setFolders}
                            setChanges={setChanges}
                        />
                    </div>
                </div>
            </div>
            <Button
                className="w-fit"
                type="submit"
                disabled={loading || !changes}
            >
                {loading ? (
                    <LoaderCircle className="size-4 text-white animate-spin" />
                ) : (
                    t("lomda-sheet.general-tab.save-btn", undefined, undefined)
                )}
            </Button>
        </form>
    );
}

const FolderDropdown = ({
    folders,
    setFolders,
    setChanges,
}: {
    folders: GetLomdaV3ResponseType["data"]["folders"];
    setFolders: Dispatch<
        SetStateAction<GetLomdaV3ResponseType["data"]["folders"]>
    >;
    setChanges: Dispatch<SetStateAction<boolean>>;
}) => {
    const { dir } = useLocale();
    const [selectedFolderId, setSelectedFolderId] = useState(
        folders[0]?.id || ""
    );
    const triggerRef = useRef<HTMLDivElement>(null);
    const [menuWidth, setMenuWidth] = useState<number>();

    useLayoutEffect(() => {
        if (triggerRef.current) {
            setMenuWidth(triggerRef.current.offsetWidth);
        }
    }, []);

    const openStorageExplorer = useStorage((state) => state.setExplorerOpen);

    const onFolderItemClick = (
        clickedOnFolder?: GetLomdaV3ResponseType["data"]["folders"][0]
    ) => {
        openStorageExplorer(true, {
            description: t("storage.explorer.select-folder.lomda.description"),
            actionsButton: {
                createFolder: true,
            },
            folders: {
                ...(clickedOnFolder && {
                    initialParentFolder: clickedOnFolder,
                }),
                onFolderClickActions: ["navigate-to-folder"],
            },
            selectedItemsMenu: {
                mode: "single-folder",
                buttons: {
                    custom: {
                        text: t("common.confirm"),
                        icon: FolderCheck,
                        onClick: (selectedFolders) => {
                            if (selectedFolders.length) {
                                const selectedFolder = selectedFolders[0];
                                setChanges(true);
                                setFolders((prev) => {
                                    if (clickedOnFolder) {
                                        if (
                                            prev.some(
                                                (f) =>
                                                    f.id === selectedFolder.id
                                            )
                                        ) {
                                            return prev;
                                        }
                                        return prev.map((f) =>
                                            f.id === clickedOnFolder.id
                                                ? selectedFolder
                                                : f
                                        );
                                    } else {
                                        return [
                                            ...prev.filter(
                                                (f) =>
                                                    f.id !== selectedFolder.id
                                            ),
                                            selectedFolder,
                                        ];
                                    }
                                });

                                setSelectedFolderId(selectedFolder.id);
                                openStorageExplorer(false);
                            }
                        },
                    },
                },
            },
        });
    };

    const currentName =
        folders.find((f) => f.id === selectedFolderId)?.name ||
        t("storage.folder-dropdown.no-selection");

    return (
        <DropdownMenu dir={dir}>
            <DropdownMenuTrigger asChild>
                <div
                    ref={triggerRef}
                    className="hover:cursor-pointer flex h-9 w-full line-clamp-1 justify-between gap-2 items-center rounded-md border border-input bg-background px-2.5 py-2 text-sm ring-offset-background"
                >
                    <p className="line-clamp-1">{currentName}</p>
                    <ChevronDown className="size-4 shrink-0" />
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                style={{ width: menuWidth }}
                className=""
            >
                {folders.map((folder) => (
                    <DropdownMenuItem
                        key={folder.id}
                        onSelect={() => onFolderItemClick(folder)}
                        className="flex items-center justify-between gap-2"
                    >
                        <div className="flex items-center gap-2">
                            <FolderEdit className="shrink-0 !size-[18px]" />
                            <p className="line-clamp-1">{folder.name}</p>
                        </div>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                const newFolders = folders.filter(
                                    (f) => f.id !== folder.id
                                );

                                setFolders(newFolders);
                                if (newFolders.length) {
                                    setSelectedFolderId(newFolders[0].id);
                                }
                                setChanges(true);
                            }}
                        >
                            <Trash />
                        </button>
                    </DropdownMenuItem>
                ))}
                {folders.length > 0 && <DropdownMenuSeparator />}
                <DropdownMenuItem
                    className="flex items-center gap-2"
                    onSelect={() => onFolderItemClick(undefined)}
                >
                    <FolderPlus className="shrink-0 !size-[18px]" />
                    {t("storage.folder-dropdown.add-folder")}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
